import { useState } from 'react';
import { Button, Switch } from '@/components/ui';
import type { SettingsDraft, TabProps } from '../../settings';
import { SettingsCard, SettingsRow } from '../SettingsPrimitives';

type Perm = NotificationPermission | 'unsupported';

/** Keys of the draft whose value is a boolean — the toggle-able fields. */
type BooleanField = {
  [K in keyof SettingsDraft]: SettingsDraft[K] extends boolean ? K : never;
}[keyof SettingsDraft];

function initialPerm(): Perm {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** A labelled toggle row bound to one boolean draft field. */
function ToggleRow({
  title,
  desc,
  field,
  draft,
  set,
}: TabProps & { title: string; desc: string; field: BooleanField }) {
  return (
    <SettingsRow
      title={title}
      desc={desc}
      control={
        <Switch checked={draft[field]} onChange={(v) => set(field, v)} aria-label={title} />
      }
    />
  );
}

export function NotificationsTab({ draft, set }: TabProps) {
  const [perm, setPerm] = useState<Perm>(initialPerm);

  function askPush() {
    if (typeof Notification === 'undefined') return;
    void Notification.requestPermission().then((p) => {
      setPerm(p);
      set('notifPush', p === 'granted');
    });
  }

  const pushDesc =
    perm === 'unsupported'
      ? 'Not supported in this browser'
      : perm === 'denied'
        ? 'Blocked — enable in browser settings'
        : perm === 'granted'
          ? 'Allowed by your browser.'
          : 'Click to allow';

  return (
    <SettingsCard title="Notifications" desc="Pick how you hear from WINR.">
      <SettingsRow
        title="Browser push"
        desc={pushDesc}
        control={
          perm === 'granted' ? (
            <Switch
              checked={draft.notifPush}
              onChange={(v) => set('notifPush', v)}
              aria-label="Browser push"
            />
          ) : perm === 'default' ? (
            <Button type="button" variant="subtle" size="sm" onClick={askPush}>
              Allow
            </Button>
          ) : (
            <Button type="button" variant="subtle" size="sm" disabled>
              Unavailable
            </Button>
          )
        }
      />

      <ToggleRow draft={draft} set={set} field="notifDM" title="Direct messages" desc="Ping me when someone sends a DM." />
      <ToggleRow draft={draft} set={set} field="notifReply" title="Replies to your posts" desc="Someone replied to a thread you started." />
      <ToggleRow draft={draft} set={set} field="notifMention" title="Mentions" desc="When someone @-tags you anywhere." />
      <ToggleRow draft={draft} set={set} field="notifLive" title="Live rooms in your communities" desc="Buzz when a host you follow goes live." />
      <ToggleRow draft={draft} set={set} field="notifDigest" title="Weekly email digest" desc="Top wins, posts, and live rooms — Friday morning." />
    </SettingsCard>
  );
}
